import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth, isSuperAdmin } from "../contexts/AuthContext";
import api from "../utils/api";
import DataGate from "../components/DataGate";
import Toast from "../components/ui/Toast";
import { newId } from "./parse";
import { channelKey, filledCells } from "./fields";
import { seedChannels } from "./seed";
import { MAX_ENTRIES, channelRef, diffChannel } from "./activity";

// The inventory comes from the API (inventory-api). Everything that reads or
// changes channels goes through this context, so the screens never talk to
// the server themselves.
//
// A change shows at once and is sent in the background, one request at a
// time, together with its Recent-page entry. If the server refuses one, the
// screen is put back to what the server has.
//
// Every change stamps the channels it touched (createdAt/By, updatedAt/By)
// and adds an entry to the activity log that the Recent page shows.

const InventoryContext = createContext(null);


export function InventoryProvider({ children }) {
  const { user } = useAuth();
  const who = user?.name || user?.email || "unknown";
  // Only a superadmin may read the change history
  const withHistory = isSuperAdmin(user);

  const [channels, setChannels] = useState([]);
  const [activity, setActivity] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | failed
  const [syncError, setSyncError] = useState(null);
  const clearSyncError = useCallback(() => setSyncError(null), []);

  const reload = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([
        api.get("/channels"),
        withHistory ? api.get("/activity") : Promise.resolve({ data: [] }),
      ]);
      setChannels(c.data);
      setActivity(a.data);
      setStatus("ready");
    } catch {
      // Already showing data: keep it rather than blanking the screen
      setStatus((s) => (s === "ready" ? s : "failed"));
    }
  }, [withHistory]);

  useEffect(() => {
    reload();
  }, [reload]);

  // In order, so an edit never reaches the server before the add it follows
  const queue = useRef(Promise.resolve());
  const send = useCallback(
    (request) => {
      queue.current = queue.current.then(request).catch(() => {
        setSyncError({ message: "The last change could not be saved. Showing what the server has." });
        reload();
      });
    },
    [reload]
  );
  const sendBatch = useCallback((batch) => send(() => api.post("/channels/batch", batch)), [send]);

  // The server sets who and when; these are for the screen until next load
  const record = useCallback(
    (entry) => {
      const full = { id: newId(), at: Date.now(), user: who, ...entry };
      setActivity((list) => [full, ...list].slice(0, MAX_ENTRIES));
      return full;
    },
    [who]
  );

  const created = useCallback(() => {
    const at = Date.now();
    return { createdAt: at, createdBy: who, updatedAt: at, updatedBy: who };
  }, [who]);

  // `via` is "add" or "duplicate", for the log
  const addChannel = useCallback(
    (data, via = "add") => {
      const channel = { ...data, id: newId(), ...created() };
      setChannels((list) => [channel, ...list]);
      const log = record({ action: via, channels: [channelRef(channel)] });
      sendBatch({ upsert: [channel], log });
      return channel;
    },
    [created, record, sendBatch]
  );

  const updateChannel = useCallback(
    (id, data) => {
      const before = channels.find((c) => c.id === id);
      if (!before) return;
      const after = { ...before, ...data, id };
      const changes = diffChannel(before, after);
      if (!changes.length) return; // saved without changing anything
      const stamped = { ...after, updatedAt: Date.now(), updatedBy: who };
      setChannels((list) => list.map((c) => (c.id === id ? stamped : c)));
      const log = record({ action: "edit", channels: [channelRef(stamped)], changes });
      sendBatch({ upsert: [stamped], log });
    },
    [channels, who, record, sendBatch]
  );

  const removeChannels = useCallback(
    (ids) => {
      const drop = new Set(ids);
      const removed = channels.filter((c) => drop.has(c.id));
      if (!removed.length) return;
      setChannels((list) => list.filter((c) => !drop.has(c.id)));
      const log = record({ action: "delete", channels: removed.map(channelRef) });
      sendBatch({ remove: removed.map((c) => c.id), log });
    },
    [channels, record, sendBatch]
  );

  // mode "merge": update channels that already exist, add the rest
  // mode "replace": the file becomes the whole inventory
  // Either way rows are matched on channelKey (partner, name, territory,
  // format), so the sheet's repeated rows become one channel, and an empty
  // cell never wipes a value (see filledCells).
  // Worked out from the current list rather than inside a state updater, so
  // the counts it returns are the ones that actually happened.
  const importChannels = useCallback(
    (rows, mode, fileName = "") => {
      if (mode === "replace") {
        const byKey = new Map();
        rows.forEach((r) => {
          const key = channelKey(r);
          const seen = byKey.get(key);
          byKey.set(key, seen ? { ...seen, ...filledCells(r) } : { ...r, id: newId(), ...created() });
        });
        const next = [...byKey.values()];
        setChannels(next);
        const log = record({
          action: "import",
          note: `${fileName ? `${fileName}, ` : ""}replaced ${channels.length} channels`,
          counts: { added: next.length, updated: 0 },
          channels: next.slice(0, 50).map(channelRef),
        });
        sendBatch({ replace: true, upsert: next, log });
        return { added: next.length, updated: 0, unchanged: 0 };
      }

      let unchanged = 0;
      const addedRefs = [];
      const updatedRefs = [];
      const written = [];
      const next = [...channels];
      const indexByKey = new Map(next.map((c, i) => [channelKey(c), i]));
      // Channels this file already added or changed, and where they are in
      // `written`: a repeated row changes the same entry, not a new one, so
      // every channel is sent and counted once
      const touched = new Map();
      const addedNow = new Set();

      rows.forEach((row) => {
        const i = indexByKey.get(channelKey(row));
        if (i === undefined) {
          const channel = { ...row, id: newId(), ...created() };
          indexByKey.set(channelKey(channel), next.length);
          next.push(channel);
          addedNow.add(channel.id);
          touched.set(channel.id, written.length);
          written.push(channel);
          addedRefs.push(channelRef(channel));
          return;
        }

        const id = next[i].id;
        const merged = { ...next[i], ...filledCells(row), id };
        if (!diffChannel(next[i], merged).length) {
          if (!touched.has(id)) unchanged += 1;
          return;
        }
        next[i] = addedNow.has(id) ? merged : { ...merged, updatedAt: Date.now(), updatedBy: who };
        if (touched.has(id)) {
          written[touched.get(id)] = next[i];
        } else {
          touched.set(id, written.length);
          written.push(next[i]);
          updatedRefs.push(channelRef(next[i]));
        }
      });

      if (written.length) {
        setChannels(next);
        const log = record({
          action: "import",
          note: fileName,
          counts: { added: addedRefs.length, updated: updatedRefs.length },
          channels: [...addedRefs, ...updatedRefs].slice(0, 50),
        });
        sendBatch({ upsert: written, log });
      }
      return { added: addedRefs.length, updated: updatedRefs.length, unchanged };
    },
    [channels, created, who, record, sendBatch]
  );

  const resetToSample = useCallback(() => {
    const next = seedChannels();
    setChannels(next);
    const log = record({ action: "reset", channels: [] });
    sendBatch({ replace: true, upsert: next, log });
  }, [record, sendBatch]);

  // Puts back an earlier list; this is what "Undo" uses. Only the channels
  // that differ from the snapshot are sent, so changes other people made to
  // other channels in the meantime are left alone. `what` says what was
  // undone, for the log.
  // Undo runs from a toast, after the change it undoes; the list is read from
  // a ref so it is the list as it is now, not as it was when the toast opened.
  const latest = useRef(channels);
  latest.current = channels;
  const restore = useCallback(
    (snapshot, what = "") => {
      const now = latest.current;
      const current = new Map(now.map((c) => [c.id, c]));
      const keep = new Set(snapshot.map((c) => c.id));
      const upsert = snapshot.filter((c) => current.get(c.id) !== c);
      const remove = now.filter((c) => !keep.has(c.id)).map((c) => c.id);
      setChannels(snapshot);
      const log = record({ action: "undo", note: what, channels: [] });
      sendBatch({ upsert, remove, log });
    },
    [record, sendBatch]
  );

  const clearActivity = useCallback(() => {
    setActivity([]);
    send(() => api.delete("/activity"));
  }, [send]);

  return (
    <InventoryContext.Provider
      value={{
        channels,
        activity,
        addChannel,
        updateChannel,
        removeChannels,
        importChannels,
        resetToSample,
        restore,
        clearActivity,
      }}
    >
      <DataGate status={status} what="the inventory" onRetry={reload}>
        {children}
      </DataGate>
      <Toast toast={syncError} onDone={clearSyncError} />
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  return useContext(InventoryContext);
}
